import { useState } from 'react';
import { Info } from 'lucide-react';
import { Button } from '../common/Button';
import type { Capability, CapabilityType, SeverityLevel } from '../../types';

interface CapabilityModalProps {
  capability: Capability | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    description: string;
    type: CapabilityType;
    severity_if_missing: SeverityLevel;
  }) => Promise<unknown>;
  onDelete?: (id: string) => Promise<unknown>;
  isLoading: boolean;
}

const typeOptions: { value: CapabilityType; label: string }[] = [
  { value: 'TECHNICAL', label: 'Technical' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'PERSONNEL', label: 'Personnel' },
];

const severityOptions: { value: SeverityLevel; label: string }[] = [
  { value: 'WARNING', label: 'Warning' },
  { value: 'BLOCKER', label: 'Blocker' },
];

const tooltips = {
  type: 'Categorizes the capability: Technical (skills, infrastructure), Business (partnerships, budgets, clearances), or Personnel (team size, expertise).',
  severity: "Controls what happens when a project requires this capability but it's not listed. Warning: flags for review. Blocker: may result in automatic rejection.",
  description: 'Provide details about this capability. The AI reads this to understand the depth and scope of what you can do.',
};

function Tooltip({ text }: { text: string }) {
  return (
    <div className="group relative inline-block ml-1">
      <Info className="w-4 h-4 text-wolf-500 cursor-help" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-xs text-wolf-200 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
        {text}
      </div>
    </div>
  );
}

export function CapabilityModal({
  capability,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isLoading,
}: CapabilityModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<CapabilityType>('TECHNICAL');
  const [severity, setSeverity] = useState<SeverityLevel>('WARNING');
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isEditing = !!capability;

  // Reset form when capability or isOpen changes (React-recommended pattern
  // for adjusting state based on props, avoids setState-in-effect)
  const [prevKey, setPrevKey] = useState<string | null>(null);
  const currentKey = `${capability?.id ?? 'new'}-${isOpen}`;
  if (currentKey !== prevKey) {
    setPrevKey(currentKey);
    if (capability) {
      setName(capability.name);
      setDescription(capability.description || '');
      setType(capability.type);
      setSeverity(capability.severity_if_missing);
    } else {
      setName('');
      setDescription('');
      setType('TECHNICAL');
      setSeverity('WARNING');
    }
    setError('');
    setShowDeleteConfirm(false);
  }

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        type,
        severity_if_missing: severity,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save capability');
    }
  };

  const handleDelete = async () => {
    if (!capability || !onDelete) return;

    try {
      await onDelete(capability.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete capability');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-wolf-800 rounded-lg p-6 w-full max-w-md border border-wolf-700">
        <h2 className="text-xl font-semibold text-white mb-6">
          {isEditing ? 'Edit Capability' : 'Add Capability'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm text-wolf-300 mb-1">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-white focus:outline-none focus:border-action"
                placeholder="e.g., Machine Learning Expertise"
                disabled={isLoading}
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="flex items-center text-sm text-wolf-300 mb-1">
                Description
                <Tooltip text={tooltips.description} />
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-white focus:outline-none focus:border-action h-24 resize-none"
                placeholder="Describe the capability in detail..."
                disabled={isLoading}
              />
            </div>

            {/* Type */}
            <div>
              <label htmlFor="type" className="flex items-center text-sm text-wolf-300 mb-1">
                Type <span className="text-red-400">*</span>
                <Tooltip text={tooltips.type} />
              </label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as CapabilityType)}
                className="w-full px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-white focus:outline-none focus:border-action"
                disabled={isLoading}
              >
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Severity */}
            <div>
              <label htmlFor="severity" className="flex items-center text-sm text-wolf-300 mb-1">
                Severity if Missing <span className="text-red-400">*</span>
                <Tooltip text={tooltips.severity} />
              </label>
              <select
                id="severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as SeverityLevel)}
                className="w-full px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-white focus:outline-none focus:border-action"
                disabled={isLoading}
              >
                {severityOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

          <div className="flex gap-3 justify-between mt-6">
            <div>
              {isEditing && onDelete && (
                showDeleteConfirm ? (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      Confirm Delete
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isLoading}
                  >
                    Delete
                  </Button>
                )
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
